import { useEffect, useState } from "react"
import { useRecoilState } from "recoil"
import { useForm } from "react-hook-form"
import * as yup from "yup"
import styled from "styled-components"
import { yupResolver } from "@hookform/resolvers/yup"
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@rentspree/component-2023.components.atoms.radio'

import RadioGroup from '@rentspree/component-2023.components.atoms.radio-group'
import Icon from "components/atoms/icon"
import Div from "components/atoms/div"
import { Text } from "components/atoms/typography"
import ScreeningOptionItemV2 from "components/molecules/screening-option-item-v2"

import { COLOR } from "styles/style-constants"
import { requiredYup } from "utils/validations/base"
import PAYER_TYPE from "constants/payer"
import PRICE from "constants/price"
import { RENTSPREE_PRE_LOGIN } from "constants/partner"
import { formReadyStateV2 } from "stores/form-ready"
import {
  LEGAL_RESTRICTIONS_LINK,
} from "constants/legal-restrictions-link"
import ButtonSetV2 from "components/molecules/button-set-v2"

const schema = yup.object().shape({
  screeningOption: yup.object().shape({
    application: requiredYup(true, yup.bool()),
    creditReport: requiredYup(true, yup.bool()),
    criminal: requiredYup(true, yup.bool()),
    eviction: requiredYup(true, yup.bool()),
    premium: requiredYup(true, yup.bool()),
  }),
  payerType: requiredYup(true),
})

const TuLogoWrapper = styled.div`
    width: 80px;
    margin: 0 8px 0 4px;
    position: relative;
    top: -5px;
`

const LinkStyled = styled.a`
  color: ${COLOR.textBlack};
  font-weight: 500;
  margin: auto 0;
  font-size: 14px;
`

const LegalRestrictionContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 5px;
  font-size: 14px;
`

const StyledText = styled(Text)`
  font-size: inherit;
`

const WhoWillPayQuestionContainer = styled(Div)`
  margin: 10px 0 5px 0;
  font-weight: 600;
`

const WhoWillPayRadioContainer = styled(RadioGroup)`
  margin-left: 10px;
  display: flex;
  flex-direction: row;
  gap: 15px;
`

const UnderLine = styled.hr`
  height: 1px;
  background-color: #ccc;
  border: none;
  @media (max-width: 520px) {
    margin: 15px 0;
  }
`

const ScreeningOptionFormV2 = ({ value, onSubmit, partner }) => {
  const [isFormError, setIsFormError] = useState(false)
  const [formReady, setFormReady] = useRecoilState(formReadyStateV2)
  const CREDIT_REPORT_PRICE = "$" + (PRICE.CREDIT_REPORT[partner] || PRICE.CREDIT_REPORT[RENTSPREE_PRE_LOGIN])
  const INCOME_VERI_PRICE = "$" + PRICE.INCOME_VERI["pre-login"]
  const PREMIUM_PRICE = "$49.99"
  const payerTypeEventMapper = {
    "renter": "applicant",
    "landlord": "property_rep"
  }
  const screeningRestrictionEventData = {
    "data-event": "button_click",
    "data-click_text": "screening_restrictions",
    "data-location": "ts_widget"
  }

  const getScreeningOptionValue = (screeningOption, field) => {
    return screeningOption ? screeningOption.creditReport && screeningOption[field] : true;
  }

  const {
    handleSubmit,
    setValue,
    getValues,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      screeningOption: {
        application: getScreeningOptionValue(value?.screeningOption, 'application'),
        creditReport: getScreeningOptionValue(value?.screeningOption, 'creditReport'),
        criminal: getScreeningOptionValue(value?.screeningOption, 'criminal'),
        eviction: getScreeningOptionValue(value?.screeningOption, 'eviction'),
        premium: getScreeningOptionValue(value?.screeningOption, 'premium'),
      },
      payerType: value?.payerType || PAYER_TYPE.RENTER,
    },
  })

  const submitHandler = (data) => {
    const { application, creditReport } = data.screeningOption
    const isReadyToSubmit = application || (creditReport && data.payerType)
    if (!isReadyToSubmit) {
      setIsFormError(true)
    } else onSubmit(data)
  }

  const { application, creditReport, criminal, eviction, premium } = watch("screeningOption")
  const payerType = watch("payerType")
  const rentalAppOptionValue = getValues("screeningOption.application")
  const creditReportOptionValue = getValues("screeningOption.creditReport")
  const criminalOptionValue = getValues("screeningOption.criminal")
  const evictionOptionValue = getValues("screeningOption.eviction")
  const premiumOptionValue = getValues("screeningOption.premium")
  const isReadyToSubmit = application || (creditReport && payerType)

  const toggleOption = (optionName) => {
    setIsFormError(false)
    const currentValue = getValues(`screeningOption.${optionName}`)
    if (optionName === "creditReport") {
      setValue("screeningOption.criminal", !currentValue, {
        shouldDirty: true,
      })
      setValue("screeningOption.eviction", !currentValue, {
        shouldDirty: true,
      })
      setValue("screeningOption.premium", !currentValue, {
        shouldDirty: true
      })
      return setValue("screeningOption.creditReport", !currentValue, {
        shouldDirty: true,
      })
    }
    if (optionName === "application") {
      setValue(`screeningOption.${optionName}`, !currentValue, {
        shouldDirty: true,
      })
    }
    if (creditReportOptionValue) {
      setValue(`screeningOption.${optionName}`, !currentValue, {
        shouldDirty: true,
      })
    }
  }

  const mapEventAttribute = () => {
    let bundleTypeEvent = ""
    let screeningItemsEventMapper = []
    if (premiumOptionValue) bundleTypeEvent = "premium"
    else {
      if (creditReportOptionValue) bundleTypeEvent = "standard"
      else if (rentalAppOptionValue) bundleTypeEvent = "application_only"
    }
    if (rentalAppOptionValue) screeningItemsEventMapper.push("rental_application")
    if (creditReportOptionValue) screeningItemsEventMapper.push("credit_report")
    if (criminalOptionValue) screeningItemsEventMapper.push("criminal")
    if (evictionOptionValue) screeningItemsEventMapper.push("eviction")
    if (premiumOptionValue) screeningItemsEventMapper.push("income_verification")
    return {
      "data-event": "ts_widget",
      "data-click_text": "next",
      "data-step": "screening_options",
      "data-application_payer": payerTypeEventMapper[payerType],
      "data-bundle": bundleTypeEvent,
      "data-screening_items": screeningItemsEventMapper.join(),
      "data-rental_application": rentalAppOptionValue,
      "data-credit_report": creditReportOptionValue,
      "data-criminal": criminalOptionValue,
      "data-eviction": evictionOptionValue,
      "data-income_verification": premiumOptionValue,
    }
  }

  useEffect(() => {
    setFormReady({ ...formReady, screeningOption: isReadyToSubmit })
  }, [isReadyToSubmit])

  return (
    <Div height="100%">
      <form style={{ height: "100%" }} onSubmit={handleSubmit(submitHandler)}>
        <Div height="100%" flex flexColumn spaceBetween >
          <Div flex flexColumn style={{ gap: "5px" }}>
            <ScreeningOptionItemV2
              id="applicationOptionItem"
              label="Rental Application"
              value={application}
              optionPrice="Free"
              fieldName="screeningOption.application"
              isShowError={isFormError}
              toggleOption={() => toggleOption("application")}
            />
            <ScreeningOptionItemV2
              id="creditReportOptionItem"
              label="Credit Report & ResidentScore®*"
              value={creditReport}
              optionPrice={CREDIT_REPORT_PRICE}
              fieldName="screeningOption.creditReport"
              isShowError={isFormError}
              toggleOption={() => toggleOption("creditReport")}
            />
            <ScreeningOptionItemV2
              id="criminalOptionItem"
              label="Criminal background check*"
              value={criminal}
              optionPrice="Included"
              fieldName="screeningOption.criminal"
              toggleOption={() => toggleOption("criminal")}
              disabled={!creditReportOptionValue}
            />
            <ScreeningOptionItemV2
              id="evictionOptionItem"
              label="Eviction related proceedings*"
              value={eviction}
              optionPrice="Included"
              fieldName="screeningOption.eviction"
              toggleOption={() => toggleOption("eviction")}
              disabled={!creditReportOptionValue}
            />
            <ScreeningOptionItemV2
              id="evictionOptionItem"
              label="Income verification report"
              value={premium}
              optionPrice={INCOME_VERI_PRICE}
              fieldName="screeningOption.premium"
              toggleOption={() => toggleOption("premium")}
              disabled={!creditReportOptionValue}
            />

            <div>
              <WhoWillPayQuestionContainer
                simple
                size={"16px"}
                color={creditReportOptionValue ? undefined : COLOR.softGray}
              >
                {`Who will pay the ${premium ? PREMIUM_PRICE : CREDIT_REPORT_PRICE} screening reports${premium ? " & documents fee" : " fee"}?`}
              </WhoWillPayQuestionContainer>
              <WhoWillPayRadioContainer
                value={creditReportOptionValue && payerType}
                onChange={(e, value) => {
                  setValue("payerType", value)
                }}
              >
                <FormControlLabel
                  label="Applicants"
                  value={PAYER_TYPE.RENTER}
                  control={<Radio disabled={!creditReportOptionValue} />}
                />
                <FormControlLabel
                  label="Me"
                  value={PAYER_TYPE.LANDLORD}
                  control={<Radio disabled={!creditReportOptionValue} />}
                />
              </WhoWillPayRadioContainer>
            </div>

            <LegalRestrictionContainer>
              <StyledText
                color={COLOR.textBlack}
                weight={400}
                margin="0"
                mMargin="0">
                * Screening reports by
              </StyledText>
              <TuLogoWrapper>
                <Icon iconName="tu-logo-with-name" size={24} width={24} height={24} />
              </TuLogoWrapper>
              <LinkStyled id="screening-restriction-link" data-testid="screening-restriction-link" href={LEGAL_RESTRICTIONS_LINK} target="_blank" {...screeningRestrictionEventData}>
                Screening Restrictions
              </LinkStyled>
            </LegalRestrictionContainer>
          </Div>

          <Div>
            <UnderLine />
            <ButtonSetV2
              primaryButton={{
                id: "submit-option-button",
                dataTestId: "submit-option-button",
                text: "Next",
                variant: "contained",
                type: "submit",
                eventData: mapEventAttribute()
              }}
            />
          </Div>
        </Div>
      </form >
    </Div >
  )
}

export default ScreeningOptionFormV2
